import type { FeatureCollection } from 'geojson';

import { apiClient } from './apiClient';
import { normaliseFeatureCollectionResponse } from './geoJsonUtils';

/**
 * Fetches air valves within the given viewport from the backend.
 * Uses GET /airvalves/{bbox} — authorised against the effective (selected) client's SAVI module.
 *
 * bbox format: "minLng,minLat,maxLng,maxLat" — matches BoundingBox.TryParse.
 * The bbox is URL-encoded as a path segment (commas → %2C).
 *
 * Passes clientId as a query parameter so the ExternalAPI can scope the response
 * to the selected client rather than the caller's JWT claim client (Origin users).
 *
 * The response is a GeoJSON FeatureCollection where each feature has a Point geometry
 * and the air-valve properties: `name`, `dma`, `highRisk` (boolean), `w3w`, `sc`.
 * Both normal and high-risk valves are returned; `highRisk` distinguishes them.
 */
export async function fetchAirValvesForBounds(clientId: string, bbox: string): Promise<FeatureCollection> {
    const encodedBbox = encodeURIComponent(bbox);
    const response = await apiClient.get<unknown>(`/airvalves/${encodedBbox}`, { params: { clientId } });
    return normaliseFeatureCollectionResponse(response.data);
}

/**
 * Fetches all high-risk air valves for the given client (not viewport-scoped).
 * Uses GET /airvalves/hr — authorised against the effective (selected) client's SAVI module.
 *
 * Used both to derive the high-risk count (legend / Layers label) and to drive the
 * "Download High Risk" export. Each feature has a Point geometry and the high-risk
 * properties: `name`, `dma`, `identified` (date), `reasons`, `w3w`, `sc`.
 */
export async function fetchHighRiskAirValves(clientId: string): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>('/airvalves/hr', { params: { clientId } });
    return normaliseFeatureCollectionResponse(response.data);
}
